import { Table, Text, Group, Stack, Center, Loader, useMantineColorScheme } from "@mantine/core";
import { Database } from "lucide-react";
import { SmartCell, evaluateFormatting } from "@bi-studio/shared";
import { useAppSelector } from "@/store/hooks";
import { selectPreviewResult, selectReportExecuting, selectSortedRows, selectReportMetadata } from "@/store/slices/reportSlice";
import * as React from "react";
import type { ReportColumn, ReportQueryResult } from "@bi-studio/shared/lib";

interface ReportResultTableProps {
    onRowClick?: (row: Record<string, unknown>) => void;
    data?: Record<string, unknown>[];
    columns?: ReportColumn[];
    isLoading?: boolean;
    previewResult?: ReportQueryResult | null;
    emptyMessage?: string;
    maxRows?: number;
    withBorders?: boolean;
}

export function ReportResultTable({ 
    onRowClick, 
    data: propData, 
    columns: propColumns, 
    isLoading: propIsLoading,
    previewResult: propPreviewResult,
    emptyMessage = "Run report to see results",
    maxRows,
    withBorders = true
}: ReportResultTableProps) {
    const { colorScheme } = useMantineColorScheme();
    
    // Redux selectors (used if props are not provided)
    const reduxPreviewResult = useAppSelector(selectPreviewResult);
    const reduxIsExecuting = useAppSelector(selectReportExecuting);
    const reduxSortedRows = useAppSelector(selectSortedRows);
    const reduxReport = useAppSelector(selectReportMetadata);

    // Determine values from props or redux
    const isExecuting = propIsLoading !== undefined ? propIsLoading : reduxIsExecuting;
    const previewResult = propPreviewResult !== undefined ? propPreviewResult : reduxPreviewResult;
    const items = propData !== undefined ? propData : (propPreviewResult ? propPreviewResult.rows : reduxSortedRows);
    
    const columns = React.useMemo(() => {
        if (propColumns) return propColumns;
        if (reduxReport?.columns && reduxReport.columns.length > 0) {
            return [...reduxReport.columns].sort((a, b) => a.order - b.order);
        }
        return previewResult?.columns || [];
    }, [propColumns, reduxReport, previewResult]);

    const displayRows = maxRows ? items.slice(0, maxRows) : items;

    return (
        <div className="overflow-x-auto" style={{ minHeight: maxRows ? 'auto' : '400px' }}>
            {isExecuting ? (
                <Center py="xl">
                    <Stack align="center">
                        <Loader size="md" />
                        <Text size="sm" c={colorScheme === 'dark' ? 'dimmed' : 'gray.7'}>Executing query...</Text>
                    </Stack>
                </Center>
            ) : (previewResult || propData) ? (
                <Table verticalSpacing="xs" highlightOnHover withColumnBorders={withBorders}>
                    <Table.Thead>
                        <Table.Tr>
                            {columns.map(col => (
                                <Table.Th key={col.name}>
                                    <Stack gap={4}>
                                        <Group wrap="nowrap" justify="space-between">
                                            <Text size="xs" fw={700} className="uppercase tracking-wider">
                                                {col.displayName || col.name}
                                            </Text>
                                        </Group>
                                    </Stack>
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {displayRows.map((row, idx) => {
                            const firstRow = items[0];
                            const lastRow = items[items.length - 1];
                            const formatting = evaluateFormatting(row, columns, firstRow, lastRow);
                            return (
                                <Table.Tr
                                    key={idx}
                                    onClick={() => onRowClick?.(row)}
                                    style={{ cursor: onRowClick ? 'pointer' : 'default', ...formatting.row }}
                                >
                                    {columns.map(col => (
                                        <Table.Td key={col.name}>
                                            <SmartCell
                                                value={row[col.name]}
                                                type={col.dataType}
                                                customStyle={formatting.cells[col.name]}
                                            />
                                        </Table.Td>
                                    ))}
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            ) : (
                <Center py="xl">
                    <Stack align="center">
                        <Database size={48} color={colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'} />
                        <Text c={colorScheme === 'dark' ? 'dimmed' : 'gray.7'} fw={500}>{emptyMessage}</Text>
                    </Stack>
                </Center>
            )}
        </div>
    );
}
